import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  FormGroup, Label, Input, Spinner, Alert, Table, Badge, Button, InputGroup,
  Pagination, PaginationItem, PaginationLink, Row, Col,
} from 'reactstrap'
import api from '../../../services/api'

const PROXY_VISIBLE = 3
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

const STATUS_FILTERS = [
  { value: 'Conflict',     label: 'Conflict',      color: 'danger',    hint: 'Both slots full — manual intervention needed' },
  { value: 'NoCandidate',  label: 'No candidate',  color: 'warning',   hint: 'Rule applies but the user has no primary SMTP to push' },
  { value: 'N/A',          label: 'N/A',            color: 'secondary', hint: 'User has no CW contact (likely disabled)' },
  { value: 'Pending',      label: 'Pending push',  color: 'info',      hint: 'Will push on next sync' },
  { value: 'FellBack',     label: 'Fell back',     color: 'success',   hint: 'Wrote / will write to the fallback slot' },
  { value: 'Synced',       label: 'Synced',        color: 'success',   hint: 'Alias already present on contact' },
  { value: 'NoRules',      label: 'No rules apply', color: 'secondary', hint: 'No rules match this user' },
]

const statusColor = (status) => STATUS_FILTERS.find(f => f.value === status)?.color || 'secondary'

const ProxyAddressList = ({ addresses }) => {
  const [expanded, setExpanded] = useState(false)
  if (!addresses || addresses.length === 0) {
    return <span className="text-muted">{'\u2014'}</span>
  }
  const shown = expanded ? addresses : addresses.slice(0, PROXY_VISIBLE)
  const hasMore = addresses.length > PROXY_VISIBLE
  return (
    <div>
      {shown.map((p, i) => (
        <div key={i} className="small">
          {p.isPrimary && <Badge color="primary" className="mr-1">Primary</Badge>}
          {p.address}
        </div>
      ))}
      {hasMore && (
        <Button color="link" size="sm" className="p-0 mt-1"
          onClick={() => setExpanded(e => !e)}>
          {expanded ? 'Show less' : `Show ${addresses.length - PROXY_VISIBLE} more\u2026`}
        </Button>
      )}
    </div>
  )
}

const CurrentCommItemsList = ({ items, missingContact }) => {
  if (missingContact) {
    return <span className="text-muted small">No CW contact</span>
  }
  if (!items || items.length === 0) {
    return <span className="text-muted">{'\u2014'}</span>
  }
  return (
    <div>
      {items.map((ci, i) => (
        <div key={i} className="small">
          <Badge color={ci.defaultFlag ? 'secondary' : 'light'} className="mr-1">
            {ci.commTypeDescription}
          </Badge>
          {ci.value}
        </div>
      ))}
    </div>
  )
}

const PaginationBar = ({ currentPage, totalPages, onPage }) => {
  if (totalPages <= 1) return null
  const windowSize = 2
  const pages = []
  for (let p = Math.max(1, currentPage - windowSize); p <= Math.min(totalPages, currentPage + windowSize); p++) {
    pages.push(p)
  }
  return (
    <Pagination size="sm" className="mb-0">
      <PaginationItem disabled={currentPage === 1}>
        <PaginationLink first onClick={() => onPage(1)} />
      </PaginationItem>
      <PaginationItem disabled={currentPage === 1}>
        <PaginationLink previous onClick={() => onPage(currentPage - 1)} />
      </PaginationItem>
      {pages[0] > 1 && (
        <PaginationItem disabled><PaginationLink>{'\u2026'}</PaginationLink></PaginationItem>
      )}
      {pages.map(p => (
        <PaginationItem key={p} active={p === currentPage}>
          <PaginationLink onClick={() => onPage(p)}>{p}</PaginationLink>
        </PaginationItem>
      ))}
      {pages[pages.length - 1] < totalPages && (
        <PaginationItem disabled><PaginationLink>{'\u2026'}</PaginationLink></PaginationItem>
      )}
      <PaginationItem disabled={currentPage === totalPages}>
        <PaginationLink next onClick={() => onPage(currentPage + 1)} />
      </PaginationItem>
      <PaginationItem disabled={currentPage === totalPages}>
        <PaginationLink last onClick={() => onPage(totalPages)} />
      </PaginationItem>
    </Pagination>
  )
}

const UsersView = () => {
  const { companyId } = useParams()

  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [upnDomainFilter, setUpnDomainFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilters, setStatusFilters] = useState(new Set())

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await api.get(`/company/${companyId}/email-sync/users`)
      if (res.ok) {
        setUsers(await res.json())
      } else {
        setError('Failed to load users')
      }
    } catch {
      setError('Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [companyId])

  useEffect(() => { load() }, [load])
  useEffect(() => { setCurrentPage(1) }, [upnDomainFilter, searchQuery, statusFilters, pageSize])

  const upnDomainOptions = useMemo(() => {
    const set = new Set(users.map(u => u.upnDomain).filter(Boolean))
    return Array.from(set).sort()
  }, [users])

  const userStatuses = (u) => {
    const set = new Set((u.ruleStates || []).map(r => r.status).filter(Boolean))
    if (set.size === 0) set.add('NoRules')
    return set
  }

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    return users.filter(u => {
      if (upnDomainFilter && u.upnDomain?.toLowerCase() !== upnDomainFilter.toLowerCase()) return false
      if (q) {
        const haystack = `${u.displayName || ''} ${u.userPrincipalName || ''}`.toLowerCase()
        if (!haystack.includes(q)) return false
      }
      if (statusFilters.size > 0) {
        const userStatusSet = userStatuses(u)
        let anyMatch = false
        for (const f of statusFilters) {
          if (userStatusSet.has(f)) { anyMatch = true; break }
        }
        if (!anyMatch) return false
      }
      return true
    })
  }, [users, upnDomainFilter, searchQuery, statusFilters])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePage = Math.min(currentPage, totalPages)
  const pageStart = (safePage - 1) * pageSize
  const pageRows = filtered.slice(pageStart, pageStart + pageSize)

  const toggleStatusFilter = (value) => {
    setStatusFilters(prev => {
      const next = new Set(prev)
      if (next.has(value)) next.delete(value)
      else next.add(value)
      return next
    })
  }

  const clearAllFilters = () => {
    setUpnDomainFilter('')
    setSearchQuery('')
    setStatusFilters(new Set())
  }

  if (loading) return <div className="text-center p-4"><Spinner color="primary" /></div>

  const anyFilterActive = !!upnDomainFilter || !!searchQuery.trim() || statusFilters.size > 0

  return (
    <div>
      {error && <Alert color="danger">{error}</Alert>}

      <Row>
        <Col md={5}>
          <FormGroup>
            <Label for="upnFilter">UPN domain</Label>
            <Input
              type="select"
              id="upnFilter"
              value={upnDomainFilter}
              onChange={(e) => setUpnDomainFilter(e.target.value)}
            >
              <option value="">All ({users.length} users)</option>
              {upnDomainOptions.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col md={7}>
          <FormGroup>
            <Label for="search">Search name or UPN</Label>
            <InputGroup>
              <Input
                id="search"
                type="text"
                placeholder="e.g. jsmith"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <Button color="link" onClick={() => setSearchQuery('')}>Clear</Button>
              )}
            </InputGroup>
          </FormGroup>
        </Col>
      </Row>

      <FormGroup>
        <Label>Status filters</Label>
        <div>
          {STATUS_FILTERS.map(f => {
            const active = statusFilters.has(f.value)
            return (
              <Badge
                key={f.value}
                color={active ? f.color : 'light'}
                className="mr-2 mb-1 p-2"
                style={{ cursor: 'pointer', userSelect: 'none', border: active ? null : '1px solid #ccc' }}
                onClick={() => toggleStatusFilter(f.value)}
                title={f.hint}
              >
                {active ? '\u2713 ' : ''}{f.label}
              </Badge>
            )
          })}
          {anyFilterActive && (
            <Button color="link" size="sm" onClick={clearAllFilters}>Clear all filters</Button>
          )}
        </div>
        <small className="text-muted">
          A user is shown if they have at least one rule outcome matching any selected status.
        </small>
      </FormGroup>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted">
          Showing {filtered.length === 0 ? 0 : pageStart + 1}
          {'\u2013'}
          {Math.min(pageStart + pageSize, filtered.length)}
          {' '}of {filtered.length}
          {anyFilterActive && filtered.length !== users.length && (
            <> (filtered from {users.length})</>
          )}
        </small>
        <div className="d-flex align-items-center">
          <small className="text-muted mr-2">Page size:</small>
          <Input
            type="select"
            bsSize="sm"
            style={{ width: 'auto' }}
            value={pageSize}
            onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
          >
            {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </Input>
        </div>
      </div>

      {filtered.length === 0 ? (
        <Alert color="info">No users match the current filters.</Alert>
      ) : (
        <Table striped responsive size="sm">
          <thead>
            <tr>
              <th>Display name</th>
              <th>UPN</th>
              <th>SMTP addresses</th>
              <th>Current CW emails</th>
              <th>Rule state</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map(u => (
              <tr key={u.userPrincipalName}>
                <td className="align-middle">{u.displayName}</td>
                <td className="align-middle">{u.userPrincipalName}</td>
                <td className="align-middle">
                  <ProxyAddressList addresses={u.proxyAddresses} />
                </td>
                <td className="align-middle">
                  <CurrentCommItemsList
                    items={u.currentEmailCommItems}
                    missingContact={u.missingCwContact}
                  />
                </td>
                <td className="align-middle">
                  {u.ruleStates?.length === 0
                    ? <span className="text-muted">No rules apply</span>
                    : u.ruleStates.map((rs, idx) => (
                      <div key={idx} className="mb-1">
                        <Badge color={statusColor(rs.status)} className="mr-1">{rs.status}</Badge>
                        <span>
                          {rs.aliasDomain}
                          {' \u2192 '}
                          {rs.preferredCommTypeDescription || `type ${rs.preferredCommTypeId}`}
                          {rs.address ? ` (${rs.address})` : ''}
                        </span>
                        {rs.detail && <div className="small text-muted">{rs.detail}</div>}
                      </div>
                    ))}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <div className="d-flex justify-content-end">
        <PaginationBar currentPage={safePage} totalPages={totalPages} onPage={setCurrentPage} />
      </div>
    </div>
  )
}

export default UsersView
